/**
 * 🩹 PATCH-350 (2026-02) · LegalPageShell
 * ---------------------------------------------------
 * Shared layout for every legal / compliance surface:
 *   · /legal/privacy      → Privacy Policy
 *   · /legal/terms        → Terms of Service
 *   · /legal/security     → Security & Compliance
 *   · /legal/hipaa        → HIPAA Notice of Privacy Practices
 *   · /legal/baa          → BAA request flow
 *   · /alliances/verified → Verified Partnerships
 *
 * Design contract:
 *   · Header + Footer identical to the marketing shell
 *   · Editorial serif prose with maximum reading width 720px
 *   · Gold rule + "LAST UPDATED" stamp per SA compliance requirement
 *   · Data-testid: `legal-page-<slug>` on the root <main>
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { ChevronLeft } from "lucide-react";

export default function LegalPageShell({
  slug,
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  children,
}) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main
      data-testid={`legal-page-${slug}`}
      className="bg-[#FAFAF7] text-[#0A0612] font-sans min-h-screen flex flex-col"
    >
      <Header />

      <section className="pt-24 pb-16 border-b border-black/5">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <Link
            to="/"
            data-testid={`legal-back-${slug}`}
            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] hover:text-[#0A0612] mb-8"
          >
            <ChevronLeft className="h-3 w-3" /> Back to home
          </Link>

          {eyebrow && (
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3">
              {eyebrow}
            </div>
          )}
          <h1
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6"
            data-testid={`legal-title-${slug}`}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg text-[#3A3A3A] leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
          <div className="mt-8 h-px w-16 bg-[#D4AF37]" />
          <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76]">
            Last updated · {lastUpdated}
          </div>
        </div>
      </section>

      <section className="flex-1 py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="legal-prose">{children}</div>
        </div>
      </section>

      {/* Prose styles scoped to this shell — serif body, gold h2 rules,
          tight lists. Kept inline so no global CSS mutation is needed. */}
      <style>{`
        .legal-prose { font-family: 'Cormorant Garamond', 'Georgia', serif; font-size: 18px; line-height: 1.7; color: #1a1a1a; }
        .legal-prose h2 { font-family: 'Cormorant Garamond', serif; font-size: 30px; font-weight: 700; margin-top: 3rem; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #D4AF37; color: #0A0612; }
        .legal-prose h3 { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 700; margin-top: 2rem; margin-bottom: 0.75rem; color: #0A0612; }
        .legal-prose p { margin-bottom: 1.25rem; }
        .legal-prose ul, .legal-prose ol { margin-bottom: 1.25rem; padding-left: 1.5rem; }
        .legal-prose li { margin-bottom: 0.5rem; }
        .legal-prose strong { font-weight: 700; color: #0A0612; }
        .legal-prose a { color: #7C5A00; text-decoration: underline; text-decoration-color: #D4AF37; text-underline-offset: 3px; }
        .legal-prose a:hover { color: #D4AF37; }
        .legal-prose code { font-family: 'IBM Plex Mono', monospace; font-size: 14px; background: #F4F1E8; padding: 2px 6px; border-radius: 2px; }
        .legal-prose blockquote { border-left: 3px solid #D4AF37; padding-left: 1rem; margin: 1.5rem 0; font-style: italic; color: #3A3A3A; }
        .legal-prose table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 15px; font-family: 'Inter', sans-serif; }
        .legal-prose th, .legal-prose td { border: 1px solid #E8E5DA; padding: 0.75rem; text-align: left; vertical-align: top; }
        .legal-prose th { background: #F4F1E8; font-weight: 600; }
        .legal-prose .callout { background: #FAF8F0; border: 1px solid #E5DEC8; border-left: 4px solid #D4AF37; padding: 1.25rem 1.5rem; margin: 1.5rem 0; font-family: 'Inter', sans-serif; font-size: 15px; line-height: 1.6; }
        .legal-prose .callout > strong:first-child { display: block; font-size: 12px; letter-spacing: 0.2em; text-transform: uppercase; color: #7C5A00; margin-bottom: 0.5rem; }
        .legal-prose .callout strong:not(:first-child) { color: #0A0612; font-weight: 700; }
      `}</style>

      <Footer />
    </main>
  );
}
